import type { Rate } from '../types';
import { formatCurrency } from '../utils/currency';

interface RateSelectorProps {
  rates: Rate[];
  selectedRateId: string | null;
  onSelect: (rate: Rate) => void;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * FedEx rate rows for the shipment rail. Renders bare (no card wrapper); the
 * host "FedEx rates" card supplies the header.
 */
function RateSelector({ rates, selectedRateId, onSelect, loading = false, disabled = false }: RateSelectorProps) {
  if (loading) {
    return (
      <div className="rate-list" aria-label="Loading rates">
        <div className="skeleton" style={{ height: '44px' }} />
        <div className="skeleton" style={{ height: '44px' }} />
        <div className="skeleton" style={{ height: '44px' }} />
      </div>
    );
  }

  if (rates.length === 0) {
    return null;
  }

  const formatDelivery = (rate: Rate) => {
    const days = rate.est_delivery_days ?? rate.delivery_days;
    if (days === undefined || days === null) return '';
    if (days === 0) return 'Same day';
    if (days === 1) return '1 business day';
    return `${days} business days`;
  };

  return (
    <div
      className="rate-list"
      role="radiogroup"
      aria-label="FedEx rates"
      aria-disabled={disabled}
    >
      {rates.map((rate) => {
        const selected = selectedRateId === rate.id;
        return (
          <label
            key={rate.id}
            className={`rate-row ${selected ? 'selected' : ''}`}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.75 : 1 }}
          >
            <input
              type="radio"
              name="rate"
              value={rate.id}
              checked={selected}
              onChange={() => onSelect(rate)}
              disabled={disabled}
            />
            <span className="radio" aria-hidden="true" />
            <span className="rate-info">
              <span className="rate-service">
                {rate.carrier} {rate.service}
              </span>
              {formatDelivery(rate) && <span className="rate-eta">Est. {formatDelivery(rate)}</span>}
            </span>
            <span className="rate-price">{formatCurrency(rate.rate, rate.currency)}</span>
          </label>
        );
      })}
    </div>
  );
}

export default RateSelector;
